import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, MessageCircle, Send } from "lucide-react";
import Reveal from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", product: "", message: "" };

const INFO = [
  { icon: MapPin, label: "Indirizzo", value: "Via Cristoforo Colombo 10, Formia (LT)" },
  { icon: Phone, label: "Telefono", value: "+39 375 827 4001", href: "tel:+393758274001" },
  {
    icon: Mail,
    label: "Email",
    value: "pomalabsrl@gmail.com",
    href: "mailto:pomalabsrl@gmail.com",
  },
];

const inputCls =
  "w-full rounded-xl border border-gold/30 bg-white/80 px-4 py-3 text-sm font-light text-ink outline-none transition-colors duration-300 placeholder:text-smoke/50 focus:border-gold";

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contacts`, form);
      toast.success("Messaggio inviato! Ti risponderemo al più presto.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Invio non riuscito. Riprova tra poco.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contatti" className="relative mx-auto max-w-7xl px-5 py-24 lg:px-10 lg:py-36">
      <div className="grid gap-14 lg:grid-cols-2 lg:gap-20">
        <Reveal>
          <span className="text-[11px] font-medium uppercase tracking-[0.42em] text-gold-deep">
            Contatti
          </span>
          <h2 className="mt-4 font-display text-4xl font-light leading-tight text-ink lg:text-6xl">
            Parliamone, <span className="italic text-gold-deep">siamo qui</span>
          </h2>
          <div className="mt-10 space-y-6">
            {INFO.map((i) => (
              <div key={i.label} className="flex items-start gap-4">
                <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gold-pale">
                  <i.icon className="h-5 w-5 text-gold-deep" />
                </span>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.28em] text-smoke">{i.label}</p>
                  {i.href ? (
                    <a href={i.href} className="mt-1 block text-base font-light text-ink hover:text-gold-deep">
                      {i.value}
                    </a>
                  ) : (
                    <p className="mt-1 text-base font-light text-ink">{i.value}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
          <a
            href="https://wa.me/393758274001?text=Buongiorno%2C%20vorrei%20informazioni%20sui%20prodotti%20Brahma%20by%20P%C3%B3malab."
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-whatsapp-button"
            className="mt-10 inline-flex items-center gap-2 rounded-full border border-gold/50 bg-white/60 px-6 py-3 text-[11px] uppercase tracking-[0.2em] text-gold-deep transition-colors duration-300 hover:bg-gold-pale"
          >
            <MessageCircle className="h-3.5 w-3.5" />
            Scrivici su WhatsApp
          </a>
        </Reveal>

        <Reveal delay={0.15}>
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="glass rounded-[2rem] border border-gold/30 p-7 shadow-[0_30px_70px_rgba(28,28,30,0.1)] lg:p-9"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="text-[10px] uppercase tracking-[0.24em] text-smoke" htmlFor="cf-name">
                  Nome *
                </label>
                <input
                  id="cf-name"
                  data-testid="contact-input-name"
                  className={`mt-1.5 ${inputCls}`}
                  value={form.name}
                  onChange={set("name")}
                  required
                  placeholder="Il tuo nome"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.24em] text-smoke" htmlFor="cf-email">
                  Email *
                </label>
                <input
                  id="cf-email"
                  type="email"
                  data-testid="contact-input-email"
                  className={`mt-1.5 ${inputCls}`}
                  value={form.email}
                  onChange={set("email")}
                  required
                  placeholder="nome@email.it"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.24em] text-smoke" htmlFor="cf-phone">
                  Telefono
                </label>
                <input
                  id="cf-phone"
                  data-testid="contact-input-phone"
                  className={`mt-1.5 ${inputCls}`}
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="+39 ..."
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.24em] text-smoke" htmlFor="cf-product">
                  Prodotto di interesse
                </label>
                <select
                  id="cf-product"
                  data-testid="contact-select-product"
                  className={`mt-1.5 ${inputCls}`}
                  value={form.product}
                  onChange={set("product")}
                >
                  <option value="">Seleziona…</option>
                  <option value="Face Cream — Brahma Golden PB">Face Cream — Brahma Golden PB</option>
                  <option value="Face Serum — Brahma Pink Lady">Face Serum — Brahma Pink Lady</option>
                  <option value="Cleansing Oil — Brahma Fall Water">
                    Cleansing Oil — Brahma Fall Water
                  </option>
                  <option value="Altro">Altro</option>
                </select>
              </div>
            </div>
            <div className="mt-4">
              <label className="text-[10px] uppercase tracking-[0.24em] text-smoke" htmlFor="cf-message">
                Messaggio *
              </label>
              <textarea
                id="cf-message"
                data-testid="contact-input-message"
                rows={5}
                className={`mt-1.5 ${inputCls} resize-none`}
                value={form.message}
                onChange={set("message")}
                required
                placeholder="Scrivici la tua richiesta…"
              />
            </div>
            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={sending}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-gold-grad px-6 py-3.5 text-[11px] uppercase tracking-[0.22em] text-white shadow-[0_14px_30px_rgba(154,123,56,0.35)] transition-all duration-300 hover:scale-[1.02] disabled:opacity-60"
            >
              <Send className="h-3.5 w-3.5" />
              {sending ? "Invio in corso…" : "Invia messaggio"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
